import { CAPABILITY } from "../../../helpers/permissions";
import {
  applyOperationalTimeLogOrgScope,
  applyUserIdsToQuery,
  resolveOperationalOrganizationId,
  resolveScopedUserIdsForQuery,
  selectOperationalTimeLogs,
} from "../../../helpers/scopedQueries";
import { getPayrollWeekBoundsFromSettings } from "../../../helpers/payrollWeek";
import { computePayrollPeriodTotals } from "../../../helpers/payrollHelpers";
import { buildGuestExecutiveBriefingData } from "../executiveBriefingGuestDemo";
import {
  formatBriefingCurrency,
  formatBriefingHours,
} from "../formatBriefingSpeech";

export const PAYROLL_PROVIDER_ID = "payroll";

async function loadPayrollSnapshot(ctx) {
  if (ctx.isGuest) {
    return buildGuestExecutiveBriefingData().payroll;
  }

  if (ctx.isOffline || !ctx.actor?.supabase_user_id) {
    return null;
  }

  const { weekStartStr, weekEndStr, todayStr } = getPayrollWeekBoundsFromSettings(
    ctx.date,
    ctx.orgSettings,
  );

  let query = selectOperationalTimeLogs(
    ctx.supabaseClient,
    "supabase_user_id, total_hours, hourly_rate, date_iso",
  )
    .gte("date_iso", weekStartStr)
    .lte("date_iso", todayStr);

  const payrollUserIds = await resolveScopedUserIdsForQuery(
    ctx.supabaseClient,
    ctx.actor,
    CAPABILITY.PAYROLL_REPORT,
    ctx.scopeContext,
  );
  const organizationId = await resolveOperationalOrganizationId(
    ctx.actor,
    ctx.supabaseClient,
    ctx.scopeContext,
  );

  query = applyUserIdsToQuery(query, payrollUserIds);
  query = applyOperationalTimeLogOrgScope(query, organizationId);

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  const totals = computePayrollPeriodTotals(data || [], ctx.orgSettings);

  return {
    grossPay: totals.grossPay,
    overtimeHours: totals.overtimeHours,
  };
}

export const payrollProvider = {
  id: PAYROLL_PROVIDER_ID,
  isEligible(ctx) {
    return ctx.can(CAPABILITY.PAYROLL_REPORT);
  },
  loadData: loadPayrollSnapshot,
  buildSection(_ctx, data) {
    if (!data) {
      return null;
    }

    const grossPhrase = formatBriefingCurrency(data.grossPay);
    const overtimePhrase = formatBriefingHours(data.overtimeHours);

    if (!grossPhrase && !overtimePhrase) {
      return "No payroll hours have been logged yet this week.";
    }

    if (grossPhrase && overtimePhrase) {
      return `This week's payroll is tracking at ${grossPhrase}, with ${overtimePhrase} of overtime so far.`;
    }

    if (grossPhrase) {
      return `This week's payroll is tracking at ${grossPhrase}.`;
    }

    return `You have ${overtimePhrase} of overtime logged this week.`;
  },
};
